// import "@/utils/sso";
import Cookies from "js-cookie"
import { getConfig } from "@/config"
import NProgress from "@/utils/progress"
import { buildHierarchyTree } from "@/utils/tree"
import remainingRouter from "./modules/remaining"
import { useMultiTagsStoreHook } from "@/store/modules/multiTags"
import { usePermissionStoreHook } from "@/store/modules/permission"
import { isUrl, openLink, storageLocal, isAllEmpty } from "@pureadmin/utils"
import {
  ascending,
  getTopMenu,
  initRouter,
  isOneOfArray,
  getHistoryMode,
  findRouteByPath,
  handleAliveRoute,
  formatTwoStageRoutes,
  formatFlatteningRoutes
} from "./utils"
import { type Router, createRouter, type RouteRecordRaw, type RouteComponent } from "vue-router"
import { type DataInfo, userKey, removeToken, multipleTabsKey } from "@/utils/auth"

/** 自动导入全部静态路由，无需再手动引入！匹配 src/router/modules 目录（任何嵌套级别）中具有 .ts 扩展名的所有文件，除了 remaining.ts 文件
 * 如何匹配所有文件请看：https://github.com/mrmlnc/fast-glob#basic-syntax
 * 如何排除文件请看：https://cn.vitejs.dev/guide/features.html#negative-patterns
 */
const modules: Record<string, any> = import.meta.glob(["./modules/**/*.ts", "!./modules/**/remaining.ts"], {
  eager: true
})

/** 原始静态路由（未做任何处理） */
const routes = []

Object.keys(modules).forEach((key) => {
  // console.log("📦 加载路由模块:", key, modules[key].default)
  routes.push(modules[key].default)
})

// console.log("🗺️ 原始路由数组:", routes)
const flatRoutes = routes.flat(Infinity)
// console.log("🔍 扁平化后:", flatRoutes)

// 分离学生端路由和其他路由
const studentRoutes = flatRoutes.filter(r => r.path === "/student")
const otherRoutes = flatRoutes.filter(r => r.path !== "/student")

// console.log("👨‍🎓 学生端路由:", studentRoutes)
// console.log("🏫 其他路由:", otherRoutes)

// 只对教师端路由进行二级拍平处理
const processedOtherRoutes = formatTwoStageRoutes(
  formatFlatteningRoutes(buildHierarchyTree(ascending(otherRoutes)))
)

/** 导出处理后的静态路由 - 学生端路由保持原样，其他路由拍平 */
export const constantRoutes: Array<RouteRecordRaw> = [
  ...studentRoutes, // 学生端路由保持原始结构
  ...processedOtherRoutes // 教师端和错误页路由拍平处理
]

// console.log("✅ 最终注册的路由:", constantRoutes)
// console.log("✅ 最终路由数量:", constantRoutes.length)
// constantRoutes.forEach(route => {
//   console.log("  - 路由:", route.path, route.name, "子路由数:", route.children?.length || 0)
// })

/** 用于渲染菜单，保持原始层级 */
export const constantMenus: Array<RouteComponent> = ascending(routes.flat(Infinity)).concat(...remainingRouter)

/** 不参与菜单的路由 */
export const remainingPaths = Object.keys(remainingRouter).map((v) => {
  return remainingRouter[v].path
})

/** 创建路由实例 */
export const router: Router = createRouter({
  history: getHistoryMode(import.meta.env.VITE_ROUTER_HISTORY),
  routes: constantRoutes.concat(...(remainingRouter as any)),
  strict: true,
  scrollBehavior(to, from, savedPosition) {
    return new Promise((resolve) => {
      if (savedPosition) {
        return savedPosition
      } else {
        if (from.meta.saveSrollTop) {
          const top: number = document.documentElement.scrollTop || document.body.scrollTop
          resolve({ left: 0, top })
        }
      }
    })
  }
})

/** 重置路由 */
export function resetRouter() {
  router.getRoutes().forEach((route) => {
    const { name, meta } = route
    if (name && router.hasRoute(name) && meta?.backstage) {
      router.removeRoute(name)
      router.options.routes = formatTwoStageRoutes(
        formatFlatteningRoutes(buildHierarchyTree(ascending(routes.flat(Infinity))))
      )
    }
  })
  usePermissionStoreHook().clearAllCachePage()
}

/** 路由白名单 */
const whiteList = ["/login"]

const { VITE_HIDE_HOME } = import.meta.env

router.beforeEach((to: any, _from: any, next) => {
  if (to.meta?.keepAlive) {
    handleAliveRoute(to, "add")
    // 页面整体刷新和点击标签页刷新
    if (_from.name === undefined || _from.name === "Redirect") {
      handleAliveRoute(to)
    }
  }
  const userInfo = storageLocal().getItem<DataInfo<number>>(userKey)
  NProgress.start()
  const externalLink = isUrl(to?.name as string)
  if (!externalLink) {
    to.matched.some((item) => {
      if (!item.meta.title) return ""
      const Title = getConfig().Title
      if (Title) document.title = `${item.meta.title} | ${Title}`
      else document.title = item.meta.title as string
    })
  }
  /** 如果已经登录并存在登录信息后不能跳转到路由白名单，而是继续保持在当前页面 */
  function toCorrectRoute() {
    whiteList.includes(to.fullPath) ? next(_from.fullPath) : next()
  }
  if (Cookies.get(multipleTabsKey) && userInfo) {
    // 无权限跳转403页面
    if (to.meta?.roles && !isOneOfArray(to.meta?.roles, userInfo?.roles)) {
      next({ path: "/error/403" })
    }
    // 开启隐藏首页后在浏览器地址栏手动输入旧的welcome路由则跳转到403
    if (VITE_HIDE_HOME === "true" && to.fullPath === "/welcome") {
      next({ path: "/error/403" })
      return
    }
    if (_from?.name) {
      // name为超链接
      if (externalLink) {
        openLink(to?.name as string)
        NProgress.done()
      } else {
        toCorrectRoute()
      }
    } else {
      // 🎯 关键修改：区分学生端和教师端路由初始化逻辑
      const roles = userInfo?.roles || []
      const isStudent = roles.includes("student")
      const isTeacher = roles.includes("teacher")

      // 学生端：使用静态路由，无需初始化动态路由和课程
      if (isStudent) {
        // console.log("🎓 路由守卫：学生端路由")
        // console.log("📍 目标路径:", to.path, "来源:", _from.path)
        // console.log("🔍 路由信息:", to)

        // 🔧 兼容旧路径：重定向旧的学生端路径到新路径
        if (to.path.startsWith("/student-")) {
          const newPath = to.path
            .replace("/student-dashboard", "/student/dashboard")
            .replace("/student-exam", "/student/exam")
            .replace("/student-practice", "/student/practice")
            .replace("/student-review", "/student/review")
            .replace("/student-score", "/student/score")
          // console.log("🔀 路由守卫：重定向旧路径", to.path, "->", newPath)
          next({ path: newPath, replace: true })
          return
        }

        // 如果学生访问根路径或教师端路径，重定向到学生首页
        if (to.path === "/" || to.path === "/welcome" || !to.path.startsWith("/student")) {
          // console.log("🔀 路由守卫：学生访问非学生端路径，重定向到学生首页")
          next({ path: "/student/dashboard", replace: true })
          return
        }
        // console.log("✅ 路由守卫：学生端路由，直接放行")
        toCorrectRoute()
        return
      }

      // 教师端：需要初始化动态路由和课程
      if (
        isTeacher &&
        !usePermissionStoreHook().menuInitialized &&
        to.path !== "/login" &&
        !to.path.startsWith("/error/")
      ) {
        // console.log("路由守卫：教师端正在初始化路由...")

        // 🎯 先确保课程数据已初始化，然后再初始化路由
        const initializeWithCourse = async () => {
          try {
            // 先初始化课程数据
            const { useCourseStore } = await import("@/store/modules/course")
            const courseStore = useCourseStore()

            // 如果课程未初始化，先初始化课程
            if (!courseStore.initialized) {
              // console.log("路由守卫：课程未初始化，先初始化课程...")
              await courseStore.initializeCourses(true) // 跳过权限刷新
            }

            // 注意：不检查是否有可用的课程，直接初始化路由。
            // 这样如果是超管（即使没有授权课程），依然可以拉取到系统管理的菜单，从而完成新库建课。
            await initRouter()
            // console.log("路由守卫：路由初始化完成，准备跳转到:", to.fullPath)
            next({ ...to, replace: true })
          } catch (error) {
            console.error("路由守卫：初始化失败:", error)
            // 初始化失败，跳转到 403 页面
            next({ path: "/error/403" })
          }
        }

        initializeWithCourse()
        return // 等待路由初始化完成
      }

      toCorrectRoute()
    }
  } else {
    if (to.path !== "/login") {
      if (whiteList.indexOf(to.path) !== -1) {
        next()
      } else {
        removeToken()
        next({ path: "/login" })
      }
    } else {
      next()
    }
  }
})

router.afterEach(() => {
  NProgress.done()
})

export default router
